import { useEffect, useMemo, useState } from "react";
import { X, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Common country codes, Guyana first (default).
const COUNTRIES = [
  { code: "+592", label: "🇬🇾 Guyana +592" },
  { code: "+1", label: "🇺🇸 USA/Canada +1" },
  { code: "+1868", label: "🇹🇹 Trinidad +1868" },
  { code: "+1246", label: "🇧🇧 Barbados +1246" },
  { code: "+44", label: "🇬🇧 UK +44" },
  { code: "+91", label: "🇮🇳 India +91" },
  { code: "+234", label: "🇳🇬 Nigeria +234" },
  { code: "+55", label: "🇧🇷 Brazil +55" },
  { code: "+27", label: "🇿🇦 South Africa +27" },
  { code: "+61", label: "🇦🇺 Australia +61" },
];

// Split an existing E.164 number into a known country code + national part.
function splitNumber(full) {
  if (!full) return { cc: "+592", national: "" };
  const sorted = [...COUNTRIES].sort((a, b) => b.code.length - a.code.length);
  for (const c of sorted) {
    if (full.startsWith(c.code)) return { cc: c.code, national: full.slice(c.code.length) };
  }
  return { cc: "+592", national: full.replace(/^\+/, "") };
}

export default function WhatsAppEarningsDialog({ open, onClose, user, onSaved }) {
  const init = useMemo(() => splitNumber(user?.whatsapp_number || ""), [user]);
  const [cc, setCc] = useState(init.cc);
  const [national, setNational] = useState(init.national);
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      const s = splitNumber(user?.whatsapp_number || "");
      setCc(s.cc);
      setNational(s.national);
      setConsent(false);
      setDone(false);
    }
    // Only re-init when the dialog opens — not when `user` changes (saving
    // updates `user` and would otherwise wipe the success screen).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const enable = async () => {
    const digits = national.replace(/\D/g, "");
    if (!digits || digits.length < 6) {
      toast.error("Enter your WhatsApp number");
      return;
    }
    if (!consent) {
      toast.error("Please tick the consent box to continue");
      return;
    }
    setSaving(true);
    try {
      const full = `${cc}${digits}`;
      const r = await api.patch("/users/me", { whatsapp_number: full, whatsapp_consent: true });
      onSaved?.(r.data);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your number");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      data-testid="whatsapp-earnings-dialog"
    >
      <div className="relative w-full max-w-md bg-[#0b0b0b] border border-[#25D366]/30 shadow-2xl">
        <button
          onClick={() => onClose?.(done)}
          data-testid="whatsapp-dialog-close"
          className="absolute right-3 top-3 text-white/50 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>

        {done ? (
          <div className="p-7 text-center" data-testid="whatsapp-dialog-success">
            <CheckCircle2 className="h-12 w-12 text-[#25D366] mx-auto mb-4" strokeWidth={1.6} />
            <h2 className="text-xl font-bold text-white mb-2">You're all set!</h2>
            <p className="text-sm text-white/70 mb-6">
              We'll notify you on WhatsApp when your videos earn money.
            </p>
            <button
              onClick={() => onClose?.(true)}
              data-testid="whatsapp-dialog-continue"
              className="w-full bg-[#25D366] text-black font-semibold py-3 hover:bg-[#1fb855] transition-colors"
            >
              Continue
            </button>
          </div>
        ) : (
          <div className="p-7">
            <h2 className="text-xl font-bold text-white mb-2">
              Get notified when you earn money 💰
            </h2>
            <p className="text-sm text-white/70 mb-5">
              Enter your WhatsApp number to get notified every time you earn money from your videos.
            </p>

            <div className="flex gap-2 mb-3">
              <select
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                data-testid="whatsapp-country-select"
                className="bg-white/5 border border-white/10 text-white text-sm px-2 py-3 focus:outline-none focus:border-[#25D366] max-w-[44%]"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.label} value={c.code} className="bg-[#0b0b0b]">
                    {c.label}
                  </option>
                ))}
              </select>
              <input
                value={national}
                onChange={(e) => setNational(e.target.value)}
                inputMode="tel"
                placeholder="659 8080"
                data-testid="whatsapp-number-input"
                className="flex-1 bg-white/5 border border-white/10 text-white placeholder:text-white/30 px-3 py-3 focus:outline-none focus:border-[#25D366] font-mono text-sm"
              />
            </div>

            <label className="flex items-start gap-2 mb-5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                data-testid="whatsapp-consent-checkbox"
                className="h-4 w-4 mt-0.5 accent-[#25D366]"
              />
              <span className="text-xs text-white/70">
                I agree to receive WhatsApp notifications about my video earnings.
              </span>
            </label>

            <button
              onClick={enable}
              disabled={saving}
              data-testid="whatsapp-enable-btn"
              className="w-full bg-[#25D366] text-black font-semibold py-3 hover:bg-[#1fb855] disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Enable WhatsApp alerts
            </button>
            <button
              onClick={() => onClose?.(false)}
              data-testid="whatsapp-maybe-later-btn"
              className="w-full text-white/50 hover:text-white/80 text-sm py-3 mt-1 transition-colors"
            >
              Maybe later
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
